import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Bar, CartesianGrid, Cell, Line, ComposedChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Check, ListX, MessageCircle, Pencil, PhoneCall, StickyNote, Target, X } from "lucide-react";
import { API } from "../lib/apiClient";
import { daysLeft } from "../lib/finance";
import { waTarget } from "../lib/wa";
import { findRenewedIds, noteHistory, callCount, isLost, LOST_NOTE, LOST_REASONS } from "../lib/renewal";
import { Modal, Empty } from "./common";

const MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const toDate = (s) => (s ? new Date(`${String(s).slice(0, 10)}T00:00:00`) : null);
const ym = (y, m) => `${y}-${String(m + 1).padStart(2, "0")}`;

export function computeRenewalProgress(policies, now = new Date(), y = now.getFullYear(), m = now.getMonth()) {
  const live = policies.filter((p) => (p.status || "aktif") !== "iptal");
  const expiring = live.filter((p) => {
    const e = toDate(p.end);
    return e && e.getFullYear() === y && e.getMonth() === m;
  });
  const renewedIds = findRenewedIds(policies);
  const isRenewed = (p) => renewedIds.has(p.id);
  const renewedList = expiring.filter(isRenewed);
  const lostCount = expiring.filter((p) => !isRenewed(p) && isLost(p)).length;
  const pending = expiring.filter((p) => !isRenewed(p) && !isLost(p)).sort((a, b) => String(a.end).localeCompare(String(b.end)));
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  return { expiring: expiring.length, renewed: renewedList.length, pending, lostCount, day: now.getDate(), daysInMonth };
}

function HistoryChart({ policies, history, currentTarget }) {
  const data = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 6 }).map((_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1);
      const s = computeRenewalProgress(policies, now, d.getFullYear(), d.getMonth());
      const key = ym(d.getFullYear(), d.getMonth());
      const isCurrent = i === 5;
      const goal = (isCurrent ? currentTarget : history[key]) || s.expiring;
      return { label: MONTHS[d.getMonth()], goal, renewed: s.renewed, pct: goal ? Math.min(100, Math.round((s.renewed / goal) * 100)) : 0, isCurrent };
    });
  }, [policies, history, currentTarget]);
  return (
    <div className="rt-history" data-testid="renewal-target-history">
      <div className="rt-history-head"><strong>Son 6 ay</strong><span>Sütun: gerçekleşen · Çizgi: hedef</span></div>
      <ResponsiveContainer width="100%" height={150}>
        <ComposedChart data={data} margin={{ top: 8, right: 4, left: -24, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="#e2e8f0" />
          <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={11} />
          <YAxis allowDecimals={false} tickLine={false} axisLine={false} fontSize={11} />
          <Tooltip formatter={(v, n) => [v, n === "renewed" ? "Yenilenen" : "Hedef"]} labelFormatter={(l, p) => `${l} · %${p?.[0]?.payload?.pct ?? 0}`} />
          <Bar dataKey="renewed" radius={[4, 4, 0, 0]} maxBarSize={28}>
            {data.map((d) => <Cell key={d.label} fill={d.isCurrent ? "#1a5fe0" : d.pct >= 100 ? "#3b82f6" : "#93c5fd"} />)}
          </Bar>
          <Line dataKey="goal" type="linear" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="rt-history-pcts">
        {data.map((d) => <span key={d.label} className={d.pct >= 100 ? "ok" : ""} data-testid={`renewal-history-${d.label}`}>%{d.pct}</span>)}
      </div>
    </div>
  );
}

const QUICK_NOTES = ["Aradım, düşünüyor", "Aradım, ulaşılamadı", "Teklif gönderildi", LOST_NOTE];

function NoteEditor({ policy, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(policy.renewalNote || "");
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [pickReason, setPickReason] = useState(false);
  const save = async (value, lostReason) => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/policies/${policy.id}/renewal-note`, lostReason !== undefined ? { note: value, lostReason } : { note: value });
      onSaved?.(data);
      setText(data.renewalNote || "");
      setEditing(false);
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Not kaydedilemedi");
    } finally {
      setBusy(false);
    }
  };
  const history = noteHistory(policy);
  const calls = callCount(policy);
  if (!editing) {
    return (
      <div className="rt-note-wrap">
        <button className={`rt-note ${policy.renewalNote ? "has" : ""}`} onClick={() => { setText(policy.renewalNote || ""); setEditing(true); }} data-testid={`renewal-note-${policy.id}`}>
          <StickyNote size={12} /> {policy.renewalNote ? <>{policy.renewalNote}{policy.renewalNoteAt && <em> · {new Date(policy.renewalNoteAt).toLocaleDateString("tr-TR")}</em>}</> : "Not ekle"}
        </button>
        {history.length > 0 && (
          <button className="rt-note-count" onClick={() => setShowHistory((s) => !s)} data-testid={`renewal-note-history-toggle-${policy.id}`}>
            <PhoneCall size={11} /> {calls} arama · {history.length} not
          </button>
        )}
        {showHistory && (
          <ol className="rt-note-history" data-testid={`renewal-note-history-${policy.id}`}>
            {[...history].reverse().map((h, i) => (
              <li key={`${h.at}-${i}`}><span>{h.at ? new Date(h.at).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "—"}</span>{h.note}</li>
            ))}
          </ol>
        )}
      </div>
    );
  }
  return (
    <div className="rt-note-edit">
      <div className="rt-note-row">
        <input value={text} maxLength={200} autoFocus placeholder="Örn: Aradım, düşünüyor" onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(text); if (e.key === "Escape") setEditing(false); }} data-testid={`renewal-note-input-${policy.id}`} />
        <button className="icon-button" disabled={busy} onClick={() => save(text)} title="Kaydet" data-testid={`renewal-note-save-${policy.id}`}><Check size={14} /></button>
        <button className="icon-button" disabled={busy} onClick={() => setEditing(false)} title="Vazgeç"><X size={14} /></button>
      </div>
      <div className="rt-note-chips">
        {!pickReason && QUICK_NOTES.map((q) => <button key={q} type="button" disabled={busy} onClick={() => (q === LOST_NOTE ? setPickReason(true) : save(q))} data-testid={`renewal-note-quick-${policy.id}-${QUICK_NOTES.indexOf(q)}`}>{q}</button>)}
        {pickReason && <>
          <span className="rt-reason-label">Kayıp nedeni:</span>
          {LOST_REASONS.map((r) => <button key={r} type="button" className="reason" disabled={busy} onClick={() => save(LOST_NOTE, r)} data-testid={`renewal-lost-reason-${policy.id}-${LOST_REASONS.indexOf(r)}`}>{r}</button>)}
          <button type="button" className="clear" onClick={() => setPickReason(false)}>Geri</button>
        </>}
        {policy.renewalNote && <button type="button" className="clear" disabled={busy} onClick={() => save("")}>Notu sil</button>}
      </div>
    </div>
  );
}

function PendingModal({ pending, lostCount, customers, onClose, onOpenCustomer, onOpenPolicy, onPolicyUpdated }) {
  const byName = useMemo(() => Object.fromEntries(customers.map((c) => [c.name, c])), [customers]);
  return (
    <Modal title={`Henüz yenilenmeyenler · ${pending.length} poliçe`} onClose={onClose}>
      {lostCount > 0 && <p className="rt-lost-note" data-testid="renewal-pending-lost-count">{lostCount} poliçe "Başka şirkete geçti" olarak işaretlendi ve listeden çıkarıldı · Yıllık Üretim › Kaybedilen müşteriler</p>}
      <p className="privacy-note" style={{ marginTop: 0 }}>Aynı müşteri + branş (araçlarda aynı plaka) için daha yeni bir poliçe girildiğinde yenilenmiş sayılır; poliçe no ve şirket değişse de fark etmez.</p>
      {pending.length === 0 ? <Empty text="Bu ay vadesi dolan tüm poliçeler yenilenmiş" /> : (
        <ul className="rt-pending-list" data-testid="renewal-pending-list">
          {pending.map((p) => {
            const c = byName[p.customer];
            const dl = daysLeft(p.end);
            const wa = waTarget(c?.phone);
            return (
              <li key={p.id} data-testid={`renewal-pending-${p.id}`}>
                <div className="rt-pending-main">
                  <button className="link-button" onClick={() => c && onOpenCustomer?.(c)} disabled={!c} data-testid={`renewal-pending-customer-${p.id}`}>{p.customer || "—"}</button>
                  <span>{p.branch}{p.plate ? ` · ${p.plate}` : ""}{p.company ? ` · ${p.company}` : ""}</span>
                  <NoteEditor policy={p} onSaved={onPolicyUpdated} />
                </div>
                <span className={`rt-due ${dl < 0 ? "late" : dl <= 7 ? "soon" : ""}`}>{dl < 0 ? `${-dl} gün geçti` : dl === 0 ? "Bugün" : `${dl} gün`}<em>{new Date(`${p.end}T00:00:00`).toLocaleDateString("tr-TR")}</em></span>
                {wa && <a className="icon-button wa" href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" title="WhatsApp" data-testid={`renewal-pending-wa-${p.id}`}><MessageCircle size={15} /></a>}
                <button className="icon-button" onClick={() => { onClose(); onOpenPolicy?.(p); }} title="Poliçeyi aç" data-testid={`renewal-pending-open-${p.id}`}><Pencil size={15} /></button>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}

export function RenewalTargetCard({ policies, customers = [], onOpenRenewals, onOpenCustomer, onOpenPolicy, onPolicyUpdated }) {
  const [target, setTarget] = useState(null);
  const [history, setHistory] = useState({});
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [showPending, setShowPending] = useState(false);
  const stats = useMemo(() => computeRenewalProgress(policies), [policies]);

  useEffect(() => {
    axios.get(`${API}/user/preferences`).then(({ data }) => {
      setTarget(data.renewalTarget || 0);
      setHistory(data.renewalTargetHistory || {});
    }).catch(() => setTarget(0));
  }, []);

  const goal = target || stats.expiring;
  const pct = goal ? Math.min(100, Math.round((stats.renewed / goal) * 100)) : 0;
  const timePct = Math.round((stats.day / stats.daysInMonth) * 100);
  const projected = stats.day ? Math.round((stats.renewed / stats.day) * stats.daysInMonth) : 0;
  const projectedCapped = Math.min(projected, Math.max(stats.expiring, goal));
  const remaining = Math.max(0, goal - stats.renewed);
  const daysLeftInMonth = stats.daysInMonth - stats.day;
  const onTrack = !goal || pct >= timePct || projectedCapped >= goal;
  const monthName = new Date().toLocaleDateString("tr-TR", { month: "long" });

  const save = async () => {
    const n = Math.max(0, Math.min(10000, parseInt(draft, 10) || 0));
    const { data } = await axios.put(`${API}/user/preferences`, { renewalTarget: n });
    setTarget(n);
    setHistory(data.renewalTargetHistory || {});
    setEditing(false);
  };

  if (target === null) return null;
  return (
    <section className="panel renewal-target-card" data-testid="renewal-target-card">
      <div className="rt-head">
        <div className="rt-title"><Target size={16} /> <h2>{monthName.charAt(0).toUpperCase() + monthName.slice(1)} yenileme hedefi</h2></div>
        {!editing ? (
          <button className="text-button" onClick={() => { setDraft(String(target || "")); setEditing(true); }} data-testid="renewal-target-edit"><Pencil size={13} /> Hedefi düzenle</button>
        ) : (
          <div className="rt-edit">
            <input type="number" min="0" value={draft} placeholder={`Otomatik (${stats.expiring})`} onChange={(e) => setDraft(e.target.value)} data-testid="renewal-target-input" autoFocus />
            <button className="icon-button" onClick={save} data-testid="renewal-target-save" title="Kaydet"><Check size={15} /></button>
            <button className="icon-button" onClick={() => setEditing(false)} data-testid="renewal-target-cancel" title="Vazgeç"><X size={15} /></button>
          </div>
        )}
      </div>
      <div className="rt-layout">
        <div>
          <div className="rt-body">
            <div className="rt-big" data-testid="renewal-target-percent">%{pct}</div>
            <div className="rt-numbers">
              <strong data-testid="renewal-target-progress">{stats.renewed} / {goal} yenileme</strong>
              <span>{target ? "Belirlediğiniz hedef" : `Otomatik hedef: bu ay vadesi dolan ${stats.expiring} poliçe`}</span>
            </div>
          </div>
          <div className="rt-bar" aria-label="Gerçekleşme">
            <div className={`rt-fill ${onTrack ? "ok" : "behind"}`} style={{ width: `${pct}%` }} />
            <div className="rt-today" style={{ left: `${timePct}%` }} title={`Ayın %${timePct}'i geçti`} />
          </div>
          <div className="rt-foot">
            <span data-testid="renewal-target-remaining">{!goal ? "Bu ay vadesi dolan poliçe yok · isterseniz bir hedef belirleyin" : remaining > 0 ? `Hedefe ${remaining} yenileme kaldı · ${daysLeftInMonth} gün` : "Hedefe ulaşıldı"}</span>
            {goal > 0 && <span className={onTrack ? "rt-ok" : "rt-behind"} data-testid="renewal-target-projection">
              Ay sonu tahmini: {projectedCapped} (%{Math.min(100, Math.round((projectedCapped / goal) * 100))}) · {onTrack ? "Hedefe doğru gidiyorsunuz" : "Tempo hedefin gerisinde"}
            </span>}
          </div>
          <div className="rt-actions">
            <button className="outline-button" onClick={() => setShowPending(true)} data-testid="renewal-target-pending-button">
              <ListX size={14} /> Yenilenmeyenler ({stats.pending.length})
            </button>
            {onOpenRenewals && <button className="text-button" onClick={onOpenRenewals} data-testid="renewal-target-open">Yenilemeleri aç →</button>}
          </div>
        </div>
        <HistoryChart policies={policies} history={history} currentTarget={target} />
      </div>
      {showPending && <PendingModal pending={stats.pending} lostCount={stats.lostCount} customers={customers} onClose={() => setShowPending(false)} onOpenCustomer={onOpenCustomer} onOpenPolicy={onOpenPolicy} onPolicyUpdated={onPolicyUpdated} />}
    </section>
  );
}
